/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

/**
 * MEDIA LIBRARY
 *
 * Centralized view of every piece of generated media for the signed-in
 * agent. Today that's video jobs only; the architecture (filter chips by
 * type, generic "media item" card shape) anticipates social posts,
 * voiceover audio, and other generated assets shipping later.
 *
 * Data source: GET /api/videos/jobs via propertyService.listVideoJobs().
 * The backend returns the agent's video jobs in descending-created order,
 * paginated. We pull up to 50 jobs per page for v1.
 *
 * Per-card actions:
 *   - Preview/Download: opens a per-format modal (one VideoJob produces
 *     up to three outputs — 9:16, 1:1, 16:9 — and the modal lets the
 *     agent pick which format to act on)
 *   - Try again: stub for now; eventually re-runs the failed job
 *
 * Thumbnails are computed on the fly from the Cloudinary URL. Cloudinary
 * supports `/video/upload/so_0/.../X.jpg` to fetch a still frame from a
 * video — no backend change needed.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Film, Search, Download, Eye, AlertCircle, RefreshCw, X as XIcon } from 'react-feather';
import propertyService from '../../services/propertyService';

// ────────────────────────────────────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────────────────────────────────────

/**
 * Cloudinary lets you grab a still frame from a video by replacing the
 * .mp4 extension with .jpg and inserting `so_<seconds>` in the transform
 * string. We use the first frame (so_0) at a modest width to keep the
 * thumbnail load light.
 *
 * If the URL doesn't match Cloudinary's pattern (e.g. self-hosted later),
 * returns null and the card falls back to a gradient placeholder.
 */
const cloudinaryVideoPoster = (cloudinaryUrl) => {
  if (!cloudinaryUrl || typeof cloudinaryUrl !== 'string') return null;
  if (!cloudinaryUrl.includes('/video/upload/')) return null;
  // Insert so_0,w_640 transforms after /upload/, swap .mp4 → .jpg
  return cloudinaryUrl
    .replace('/video/upload/', '/video/upload/so_0,w_640,c_fill/')
    .replace(/\.mp4(\?.*)?$/, '.jpg$1');
};

/**
 * Cloudinary force-download URL — flips Content-Disposition to attachment
 * so clicking saves rather than playing inline. Same helper that ships
 * in the backend email payload, replicated here for the in-app download
 * button (no backend round-trip).
 */
const cloudinaryDownloadUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('/fl_attachment')) return url;
  return url.replace('/upload/', '/upload/fl_attachment/');
};

/**
 * Human-readable relative time. Avoids pulling in a date library for one
 * label. "Just now" / "5 min ago" / "2 hours ago" / "Yesterday" / "Mar 14".
 */
const relativeTime = (date) => {
  if (!date) return '';
  const then = new Date(date).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`;
  if (diffSec < 172800) return 'Yesterday';
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} days ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Pick the canonical thumbnail URL for a video job: prefer the 16:9
 * output (best aspect ratio for cards), fall back to whatever's there.
 */
const pickThumbnailSource = (outputs) => {
  if (!Array.isArray(outputs) || outputs.length === 0) return null;
  const order = ['16:9', '1:1', '9:16'];
  for (const fmt of order) {
    const match = outputs.find((o) => o?.format === fmt && o?.cloudinaryUrl);
    if (match) return match.cloudinaryUrl;
  }
  return outputs[0]?.cloudinaryUrl || null;
};

// ────────────────────────────────────────────────────────────────────────────
// PER-FORMAT MODAL
// One VideoJob → up to 3 format outputs. Modal lets the agent pick which
// to preview / download.
// ────────────────────────────────────────────────────────────────────────────

const FORMAT_META = {
  '9:16': { label: 'Vertical', subtitle: 'Reels, TikTok, Shorts' },
  '1:1':  { label: 'Square',   subtitle: 'Feeds, ads' },
  '16:9': { label: 'Landscape', subtitle: 'YouTube, websites' },
};

const FormatPickerModal = ({ item, onClose, onRemoved }) => {
  // Inline confirm pattern — the "Remove" button transforms into
  // "Confirm" + "Cancel" on first click rather than opening a separate
  // dialog. Less disruptive than a nested modal and reads as a single
  // intentional action.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  // Reset internal state when modal opens for a different item (or closes
  // and reopens). Without this, a previous "confirming" state would
  // persist into a fresh open.
  useEffect(() => {
    setConfirmingRemove(false);
    setRemoving(false);
    setRemoveError('');
  }, [item?._id]);

  if (!item) return null;

  const handleRemove = async () => {
    if (!confirmingRemove) {
      setConfirmingRemove(true);
      return;
    }
    setRemoving(true);
    setRemoveError('');
    try {
      await propertyService.hideVideoJob(item._id);
      // Tell the parent the item is gone — parent removes from state and
      // closes the modal. The modal does NOT call onClose itself because
      // the parent's onRemoved logic does that (and avoids a flash of the
      // now-stale item between calls).
      if (onRemoved) onRemoved(item._id);
    } catch (err) {
      console.error('[MediaLibrary] remove failed', err);
      setRemoveError(
        err?.response?.data?.message ||
        err?.message ||
        'Could not remove from library'
      );
      setRemoving(false);
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        zIndex: 999999, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff', borderRadius: '16px', maxWidth: '480px',
          width: '100%', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid rgba(15,23,42,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: '1rem', fontWeight: 600, color: '#0f172a',
            }}>{item.listing?.address || 'Listing video'}</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
              Pick a format to preview or download
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 4, color: '#64748b',
            }}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 24px 24px' }}>
          {(item.outputs || []).map((o) => {
            const meta = FORMAT_META[o.format] || { label: o.format, subtitle: '' };
            // Generate a thumbnail URL from the video's Cloudinary URL.
            // Falls back to null if the URL pattern doesn't match — we
            // render a tinted placeholder block in that case so the row
            // layout stays consistent across formats.
            const thumbUrl = cloudinaryVideoPoster(o.cloudinaryUrl);
            return (
              <div
                key={o.format}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px', marginBottom: '10px',
                  background: '#f8f7f3', borderRadius: '12px',
                  border: '1px solid #ece8df',
                }}
              >
                {/* Format thumbnail — square crop so all three formats sit
                    at uniform width in the row. The thumbnail conveys
                    "this is the video"; the format-aspect info comes from
                    the label and subtitle. Cover-fit means a 9:16 video
                    gets its center crop, a 16:9 video gets its center
                    crop, square is unchanged. */}
                <div style={{
                  width: '64px', height: '64px',
                  flexShrink: 0,
                  borderRadius: '8px', overflow: 'hidden',
                  background: '#1f2937',
                  position: 'relative',
                }}>
                  {thumbUrl && (
                    <img
                      src={thumbUrl}
                      alt=""
                      style={{
                        width: '100%', height: '100%', objectFit: 'cover',
                        display: 'block',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {/* Subtle play-glyph centered on the thumbnail so it
                      reads as "video" not just "image" at a glance. */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="rgba(255,255,255,0.92)" aria-hidden="true">
                      <polygon points="6,3 21,12 6,21" />
                    </svg>
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: '0 0 2px', fontSize: '0.88rem', fontWeight: 600,
                    color: '#2a2d2a',
                  }}>{meta.label} · {o.format}</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>
                    {meta.subtitle}
                  </p>
                </div>
                <a
                  href={o.cloudinaryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '7px 14px', background: '#ffffff',
                    border: '1px solid #d6d1c5', borderRadius: '8px',
                    fontSize: '0.78rem', fontWeight: 500, color: '#2a2d2a',
                    textDecoration: 'none', display: 'inline-flex',
                    alignItems: 'center', gap: '4px',
                  }}
                >
                  <Eye size={13} /> Preview
                </a>
                <a
                  href={cloudinaryDownloadUrl(o.cloudinaryUrl)}
                  style={{
                    padding: '7px 14px', background: '#2a2d2a',
                    border: '1px solid #2a2d2a', borderRadius: '8px',
                    fontSize: '0.78rem', fontWeight: 500, color: '#ffffff',
                    textDecoration: 'none', display: 'inline-flex',
                    alignItems: 'center', gap: '4px',
                  }}
                >
                  <Download size={13} /> Download
                </a>
              </div>
            );
          })}

          {(!item.outputs || item.outputs.length === 0) && (
            <div style={{
              padding: '24px', textAlign: 'center', color: '#64748b',
              fontSize: '0.85rem',
            }}>
              No formats available for this video yet.
            </div>
          )}

          {/* Remove from library — soft delete via hideVideoJob. The
              video stays in the backend but disappears from this agent's
              library. Inline confirmation pattern: first click flips to
              a "Confirm" + "Cancel" state; second confirms. Cleaner than
              a nested modal dialog. */}
          <div style={{
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid #ece8df',
          }}>
            {removeError && (
              <p style={{
                margin: '0 0 10px',
                fontSize: '0.78rem',
                color: '#991b1b',
              }}>
                {removeError}
              </p>
            )}
            {!confirmingRemove ? (
              <button
                onClick={handleRemove}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  color: '#94a3b8',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                Remove from library
              </button>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                flexWrap: 'wrap',
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '0.82rem',
                  color: '#475569',
                  flex: 1,
                }}>
                  Remove this video from your library?
                </p>
                <button
                  onClick={() => setConfirmingRemove(false)}
                  disabled={removing}
                  style={{
                    padding: '7px 14px', background: '#ffffff',
                    border: '1px solid #d6d1c5', borderRadius: '8px',
                    fontSize: '0.78rem', fontWeight: 500, color: '#2a2d2a',
                    cursor: removing ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemove}
                  disabled={removing}
                  style={{
                    padding: '7px 14px',
                    background: removing ? '#cbd5e1' : '#b91c1c',
                    border: removing ? '1px solid #cbd5e1' : '1px solid #b91c1c',
                    borderRadius: '8px',
                    fontSize: '0.78rem', fontWeight: 500, color: '#ffffff',
                    cursor: removing ? 'not-allowed' : 'pointer',
                  }}
                >
                  {removing ? 'Removing…' : 'Remove'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ────────────────────────────────────────────────────────────────────────────
// SINGLE CARD
// One card per VideoJob — thumbnail, format chips overlay, address, status.
// ────────────────────────────────────────────────────────────────────────────

const MediaCard = ({ item, onOpen, onRemove }) => {
  // Failed cards offer Remove directly on the card since they don't open
  // the format-picker modal. Same inline-confirm pattern as the modal —
  // first click transforms the button into Confirm/Cancel.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const triggerRemove = async () => {
    if (!confirmingRemove) {
      setConfirmingRemove(true);
      return;
    }
    setRemoving(true);
    try {
      await onRemove(item._id);
      // No need to reset state — the card will be unmounted by the parent
      // when the item is removed from `mediaItems`.
    } catch (err) {
      // Parent surfaces errors; we just clear our local "removing" state
      // so the buttons are usable again.
      console.error('[MediaLibrary] card remove failed', err);
      setRemoving(false);
      setConfirmingRemove(false);
    }
  };
  const isReady = item.status === 'complete';
  const isGenerating = ['queued', 'generating'].includes(item.status);
  const isFailed = item.status === 'failed';

  const thumbSource = pickThumbnailSource(item.outputs);
  const thumbUrl = thumbSource ? cloudinaryVideoPoster(thumbSource) : null;

  const formatChips = (item.outputs || [])
    .map((o) => o.format)
    .filter(Boolean);

  // Status pill colors mirror the agent portal's existing pastel system
  const statusStyles = isReady
    ? { background: '#DCFCE7', color: '#166534', label: 'Ready' }
    : isGenerating
      ? { background: '#FEF3C7', color: '#92400E', label: 'Generating' }
      : isFailed
        ? { background: '#FEE2E2', color: '#991b1b', label: 'Failed' }
        : { background: '#E5E5E5', color: '#4b5563', label: item.status || 'Pending' };

  return (
    <div style={{
      background: '#ffffff', borderRadius: '14px',
      border: '1px solid #e6e1d8', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Thumbnail region */}
      <div style={{
        position: 'relative', aspectRatio: '16 / 9',
        background: thumbUrl ? '#1f2937' : 'linear-gradient(135deg, #4a5a6b 0%, #2a3540 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {thumbUrl && isReady && (
          <img
            src={thumbUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}

        {isReady && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: thumbUrl ? 'rgba(0,0,0,0.18)' : 'transparent',
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="rgba(255,255,255,0.92)" aria-hidden="true">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          </div>
        )}

        {isGenerating && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: '10px', color: 'rgba(255,255,255,0.85)',
          }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.85)', borderTopColor: 'transparent',
              animation: 'media-library-spin 1s linear infinite',
            }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 500 }}>Generating</span>
          </div>
        )}

        {isFailed && (
          <AlertCircle size={32} color="#fca5a5" aria-hidden="true" />
        )}

        {/* Type badge */}
        <div style={{
          position: 'absolute', top: 8, left: 8,
          padding: '3px 8px', background: 'rgba(0,0,0,0.55)', color: '#ffffff',
          fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.04em',
          borderRadius: '999px',
        }}>VIDEO</div>

        {/* Status pill */}
        <div style={{
          position: 'absolute', top: 8, right: 8,
          padding: '3px 8px', borderRadius: '999px',
          background: statusStyles.background, color: statusStyles.color,
          fontSize: '0.65rem', fontWeight: 600,
        }}>{statusStyles.label}</div>

        {/* Format chips (only on ready cards — generating/failed don't have outputs yet) */}
        {isReady && formatChips.length > 0 && (
          <div style={{
            position: 'absolute', bottom: 8, left: 8,
            display: 'flex', gap: '4px',
          }}>
            {formatChips.map((f) => (
              <span key={f} style={{
                padding: '2px 6px', background: 'rgba(0,0,0,0.55)',
                color: '#ffffff', fontSize: '0.62rem', fontWeight: 500,
                borderRadius: '4px',
              }}>{f}</span>
            ))}
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div>
          <p style={{
            margin: '0 0 2px', fontSize: '0.88rem', fontWeight: 600,
            color: '#2a2d2a', overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {item.listing?.address || 'Untitled listing'}
          </p>
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#8a857d' }}>
            {item.listing?.csz ? `${item.listing.csz} · ` : ''}
            {relativeTime(item.createdAt)}
            {isFailed && item.pointsRefunded ? ' · Refunded' : ''}
          </p>
        </div>

        {isReady && (
          <button
            onClick={() => onOpen(item)}
            style={{
              padding: '8px', background: '#2a2d2a', color: '#ffffff',
              border: '1px solid #2a2d2a', borderRadius: '8px',
              fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            <Eye size={13} /> View formats
          </button>
        )}

        {isGenerating && (
          <button
            disabled
            style={{
              padding: '8px', background: '#f3f1ec', color: '#b4b0a7',
              border: '1px solid #e6e1d8', borderRadius: '8px',
              fontSize: '0.78rem', fontWeight: 500, cursor: 'not-allowed',
            }}
          >
            Generating…
          </button>
        )}

        {isFailed && !confirmingRemove && (
          <button
            onClick={triggerRemove}
            style={{
              padding: '8px', background: '#ffffff', color: '#2a2d2a',
              border: '1px solid #d6d1c5', borderRadius: '8px',
              fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            <RefreshCw size={13} /> Remove
          </button>
        )}

        {isFailed && confirmingRemove && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setConfirmingRemove(false)}
              disabled={removing}
              style={{
                flex: 1,
                padding: '8px', background: '#ffffff', color: '#2a2d2a',
                border: '1px solid #d6d1c5', borderRadius: '8px',
                fontSize: '0.78rem', fontWeight: 500,
                cursor: removing ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={triggerRemove}
              disabled={removing}
              style={{
                flex: 1,
                padding: '8px',
                background: removing ? '#cbd5e1' : '#b91c1c',
                border: removing ? '1px solid #cbd5e1' : '1px solid #b91c1c',
                borderRadius: '8px',
                fontSize: '0.78rem', fontWeight: 500, color: '#ffffff',
                cursor: removing ? 'not-allowed' : 'pointer',
              }}
            >
              {removing ? '…' : 'Remove'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ────────────────────────────────────────────────────────────────────────────

const MediaLibrary = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state. typeFilter is forward-compatible for posts/audio later.
  const [typeFilter, setTypeFilter] = useState('all');     // 'all' | 'video' | ('post' future)
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'ready' | 'generating' | 'failed'
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);

  /**
   * Remove an item from the agent's library. Called from two places:
   *   - FormatPickerModal (modal already called the API, just needs us
   *     to drop the item from state and close itself). Pass via the
   *     `onRemoved` prop.
   *   - Failed MediaCard (hasn't called the API yet — it delegates to
   *     this function via `onRemove`, which does the API call here).
   *     Throws on failure so the card can handle it.
   *
   * `alreadyHidden` differentiates: if the caller already hit the API
   * (the modal), we skip the duplicate request.
   */
  const removeItem = async (jobId, { alreadyHidden = false } = {}) => {
    if (!alreadyHidden) {
      // Failed-card path: we own the API call.
      await propertyService.hideVideoJob(jobId);
    }
    // Drop the item from local state. The card unmounts immediately so
    // the agent never sees the "removed" item linger.
    setMediaItems((prev) => prev.filter((m) => m._id !== jobId));
    // If this was the open modal item, close it.
    setSelectedItem((prev) => (prev?._id === jobId ? null : prev));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // Backend route: GET /api/videos/jobs (already exists per
        // videoController.listVideoJobs). Frontend service method assumed
        // to exist on propertyService. If it doesn't, add this stub:
        //   listVideoJobs: (params) =>
        //     api.get('/api/videos/jobs', { params }).then(r => r.data)
        // to propertyService.js.
        const data = await propertyService.listVideoJobs({ limit: 50 });
        if (cancelled) return;
        // Backend may return either an array directly, or
        // { jobs: [...], total, page, limit }. Handle both shapes.
        const jobs = Array.isArray(data) ? data : (data?.jobs || []);
        setMediaItems(jobs);
      } catch (err) {
        if (cancelled) return;
        console.error('[MediaLibrary] failed to load jobs', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to load your media');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Compute filter counts (independent of current filter — these show the
  // total count for each filter chip regardless of what's selected).
  const counts = useMemo(() => ({
    all: mediaItems.length,
    video: mediaItems.length, // every item is video for v1
    ready: mediaItems.filter((m) => m.status === 'complete').length,
    generating: mediaItems.filter((m) => ['queued', 'generating'].includes(m.status)).length,
    failed: mediaItems.filter((m) => m.status === 'failed').length,
  }), [mediaItems]);

  // Apply filters in a single pass
  const filteredItems = useMemo(() => {
    return mediaItems.filter((m) => {
      // Type filter
      if (typeFilter !== 'all') {
        // Today everything is video; the check is a no-op but kept for
        // forward compatibility when items have a .mediaType field.
        if (typeFilter === 'video' && m.mediaType && m.mediaType !== 'video') return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'ready' && m.status !== 'complete') return false;
        if (statusFilter === 'generating' && !['queued', 'generating'].includes(m.status)) return false;
        if (statusFilter === 'failed' && m.status !== 'failed') return false;
      }

      // Search filter — match against address, csz
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const addressMatch = m.listing?.address?.toLowerCase().includes(q);
        const cszMatch = m.listing?.csz?.toLowerCase().includes(q);
        if (!addressMatch && !cszMatch) return false;
      }

      return true;
    });
  }, [mediaItems, typeFilter, statusFilter, searchQuery]);

  // Chip component — DRY helper for the filter row.
  const Chip = ({ active, onClick, disabled, label, count, last }) => (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: '7px 14px',
        background: active ? '#2a2d2a' : 'transparent',
        color: disabled ? '#b4b0a7' : (active ? '#ffffff' : '#2a2d2a'),
        border: `1px solid ${disabled ? '#e6e1d8' : (active ? '#2a2d2a' : '#d6d1c5')}`,
        borderRadius: '999px',
        fontSize: '0.82rem',
        fontWeight: active ? 600 : 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        marginRight: last ? 0 : '8px',
      }}
    >
      {label}
      <span style={{ opacity: 0.6, fontSize: '0.72rem' }}>{count}</span>
    </button>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: '1280px', margin: '0 auto' }}>
      {/* Spin animation lives here as a single shared keyframe */}
      <style>{`
        @keyframes media-library-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: '24px',
        marginBottom: '8px', flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{
            margin: '0 0 4px', fontSize: '1.5rem', fontWeight: 600,
            color: '#0f172a', letterSpacing: '-0.01em',
          }}>
            Media Library
          </h1>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
            Every video, post, and asset you've generated for your listings.
          </p>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', minWidth: '240px' }}>
          <Search
            size={14}
            style={{
              position: 'absolute', left: '12px', top: '50%',
              transform: 'translateY(-50%)', color: '#94a3b8',
            }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by address"
            style={{
              width: '100%', height: '36px', padding: '0 12px 0 32px',
              border: '1px solid #e6e1d8', borderRadius: '8px',
              fontSize: '0.85rem', background: '#ffffff', outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Filter chips */}
      <div style={{
        display: 'flex', gap: '0', flexWrap: 'wrap', alignItems: 'center',
        margin: '20px 0 24px',
      }}>
        {/* Type group */}
        <Chip
          active={typeFilter === 'all'}
          onClick={() => setTypeFilter('all')}
          label="All"
          count={counts.all}
        />
        <Chip
          active={typeFilter === 'video'}
          onClick={() => setTypeFilter('video')}
          label="Videos"
          count={counts.video}
        />
        <Chip
          disabled
          label="Posts"
          count="soon"
        />

        {/* Visual divider */}
        <div style={{
          width: '1px', height: '20px', background: '#e6e1d8',
          margin: '0 8px',
        }} />

        {/* Status group */}
        <Chip
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          label="Any status"
          count={counts.all}
        />
        <Chip
          active={statusFilter === 'ready'}
          onClick={() => setStatusFilter('ready')}
          label="Ready"
          count={counts.ready}
        />
        <Chip
          active={statusFilter === 'generating'}
          onClick={() => setStatusFilter('generating')}
          label="Generating"
          count={counts.generating}
        />
        <Chip
          active={statusFilter === 'failed'}
          onClick={() => setStatusFilter('failed')}
          label="Failed"
          count={counts.failed}
          last
        />
      </div>

      {/* Body — loading / error / empty / grid */}
      {loading ? (
        <div style={{
          padding: '80px 0', textAlign: 'center', color: '#94a3b8',
          fontSize: '0.9rem',
        }}>
          Loading your media…
        </div>
      ) : error ? (
        <div style={{
          padding: '20px 24px', background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: '12px',
          color: '#991b1b', fontSize: '0.88rem',
        }}>
          {error}
        </div>
      ) : mediaItems.length === 0 ? (
        // True empty state — no media exists at all
        <div style={{
          padding: '80px 24px', textAlign: 'center',
          background: '#ffffff', border: '1px solid #e6e1d8',
          borderRadius: '14px',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: '#f3f1ec', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            marginBottom: '16px', color: '#94a3b8',
          }}>
            <Film size={22} />
          </div>
          <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
            Nothing here yet
          </h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', maxWidth: '380px', marginLeft: 'auto', marginRight: 'auto' }}>
            Your generated videos and content will appear here. Head to Live Listings to create your first video from one of your properties.
          </p>
        </div>
      ) : filteredItems.length === 0 ? (
        // Filtered empty state — items exist but filters hide them all
        <div style={{
          padding: '60px 24px', textAlign: 'center',
          background: '#ffffff', border: '1px solid #e6e1d8',
          borderRadius: '14px',
        }}>
          <p style={{ margin: '0 0 4px', fontSize: '0.92rem', color: '#0f172a', fontWeight: 500 }}>
            No matches
          </p>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
            Try a different filter or clear your search.
          </p>
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px',
          }}>
            {filteredItems.map((item) => (
              <MediaCard
                key={item._id}
                item={item}
                onOpen={setSelectedItem}
                onRemove={removeItem}
              />
            ))}
          </div>

          <p style={{
            margin: '24px 0 0', fontSize: '0.78rem',
            color: '#94a3b8', textAlign: 'center',
          }}>
            Showing {filteredItems.length} of {mediaItems.length}
          </p>
        </>
      )}

      {/* Per-format picker modal */}
      <FormatPickerModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onRemoved={(id) => removeItem(id, { alreadyHidden: true })}
      />
    </div>
  );
};

export default MediaLibrary;